/**
 * @escapehub/token-creator - Token Deployment Functions
 *
 * Framework-agnostic token deployment using ethers.js v6.
 * No Angular dependencies - works with any JavaScript/TypeScript environment.
 *
 * @example React usage with wagmi
 * ```tsx
 * import { deployToken, normalizeWallet, createDefaultConfig } from '@escapehub/token-creator';
 * import { useWalletClient } from 'wagmi';
 *
 * function DeployButton() {
 *   const { data: walletClient } = useWalletClient();
 *
 *   const handleDeploy = async () => {
 *     const signer = await normalizeWallet(walletClient);
 *     const config = createDefaultConfig('My Token', 'MTK', '1000000', '0x...');
 *     const result = await deployToken(signer, config, { chainId: 1, factoryAddress: '0x...' });
 *     console.log('Deployed at:', result.tokenAddress);
 *   };
 *
 *   return <button onClick={handleDeploy}>Deploy Token</button>;
 * }
 * ```
 *
 * @example Vue usage with ethers
 * ```vue
 * <script setup>
 * import { deployToken, normalizeWallet, createDefaultConfig } from '@escapehub/token-creator';
 * import { BrowserProvider } from 'ethers';
 *
 * const deploy = async () => {
 *   const provider = new BrowserProvider(window.ethereum);
 *   const signer = await normalizeWallet({ provider: window.ethereum, account: '0x...' });
 *   const config = createDefaultConfig('My Token', 'MTK', '1000000', '0x...');
 *   const result = await deployToken(signer, config, { chainId: 1, factoryAddress: '0x...' });
 *   console.log('Deployed at:', result.tokenAddress);
 * };
 * </script>
 * ```
 *
 * @example Vanilla JS usage
 * ```javascript
 * import { deployToken, normalizeWallet, createDefaultConfig } from '@escapehub/token-creator';
 *
 * async function deploy() {
 *   const signer = await normalizeWallet(window.ethereum);
 *   const config = createDefaultConfig('My Token', 'MTK', '1000000', '0x...');
 *   const result = await deployToken(signer, config, { chainId: 1, factoryAddress: '0x...' });
 *   console.log('Deployed at:', result.tokenAddress);
 * }
 * ```
 */

import {
  Contract,
  Signer,
  BrowserProvider,
  JsonRpcProvider,
  Provider,
  keccak256,
  getBytes,
  getCreate2Address,
  Interface,
  TransactionReceipt,
} from 'ethers';
import type {
  TokenConfig,
  Address,
} from './types/deploy-config';
import { ZERO_ADDRESS } from './types/deploy-config';
import type { ChainConfig } from './types/token-data';

/**
 * Result of a successful token deployment.
 */
export interface DeploymentResult {
  /** Transaction hash of the deployment */
  readonly txHash: `0x${string}`;
  /** Deployed token contract address */
  readonly tokenAddress: Address;
}

/**
 * Gas estimation for a token deployment.
 */
export interface GasEstimation {
  /** Estimated gas limit */
  readonly gasLimit: bigint;
  /** Factory creation fee in wei */
  readonly creationFee: bigint;
}

// ============================================================================
// Factory ABI (Human-Readable Format)
// ============================================================================

/**
 * Human-readable ABI for LaunchERC20Factory contract.
 * Contains only the functions needed for token deployment.
 */
export const FACTORY_ABI = [
  // createToken function
  'function createToken((string name, string symbol, uint8 decimals, uint256 initialSupply, address finalOwner, bool mintingEnabled, uint256 hardCap, bool pausable, bool rescueEnabled, bool burnEnabled, bool metadataMutable, bool socialsMutable, bool feesModuleEnabled, bool limitsModuleEnabled, bool cooldownModuleEnabled, bool blacklistModuleEnabled, string website, string telegram, string xHandle, string discord, bool feesEnabled, uint16 maxBuyFeeBps, uint16 maxSellFeeBps, uint16 maxTransferFeeBps, uint16 buyFeeBps, uint16 sellFeeBps, uint16 transferFeeBps, uint16 feeBurnShareBps, address feeRecipient, uint256 maxWalletAmount, uint256 maxTxAmount, uint64 limitsEndTime, bool exemptRecipientsFromLimits, address[] additionalLimitExempt, uint32 maxCooldownSeconds, uint32 cooldownSeconds, address[] initialRecipients, uint256[] initialAmounts, string[] initialLabels, uint64 tradingSchedule, bool enableTradingImmediately, address router, bool routerProtectionEnabled, address[] initialBlacklist, bool freezeBlacklistAfterDeploy, bool renounceOwnership, bool useSalt, bytes32 salt, bool deadblocksEnabled, uint8 deadBlocks, uint8 deadblockMode, bool deadblockExemptLimitExempt, bool whitelistModuleEnabled, uint8 whitelistMode, bool whitelistAutoDisable, bool whitelistBypassesRouter, bool whitelistMutable, address[] initialWhitelist, bool freezeWhitelistAfterDeploy, bool antiDumpModuleEnabled, uint8 antiDumpType, uint256 maxAntiDumpLimit, uint64 maxAntiDumpPeriod, uint256 antiDumpLimit, uint64 antiDumpPeriod, uint64 antiDumpEndTime, uint8 batchTransferAccess, uint8 batchTransferFees, uint16 maxBatchSize, bool permitEnabled, bool freezeFeesAfterDeploy, bool freezeLimitsAfterDeploy, bool freezeCooldownAfterDeploy, bool freezeAntiDumpAfterDeploy, bool blockOwnershipTransferAfterDeploy) p) payable returns (address)',
  // getCreationFee function
  'function getCreationFee() view returns (uint256 fee)',
  // predictAddress function
  'function predictAddress(bytes32 salt) view returns (address)',
  // implementation function
  'function implementation() view returns (address)',
  // TokenCreated event
  'event TokenCreated(address indexed token, address indexed owner, bytes32 salt)',
] as const;

/**
 * Full JSON ABI for LaunchERC20Factory contract.
 * Use this if you need the full ABI structure for contract interactions.
 */
export const FACTORY_ABI_JSON = [
  {
    type: 'function',
    name: 'createToken',
    inputs: [
      {
        name: 'p',
        type: 'tuple',
        components: [
          { name: 'name', type: 'string' },
          { name: 'symbol', type: 'string' },
          { name: 'decimals', type: 'uint8' },
          { name: 'initialSupply', type: 'uint256' },
          { name: 'finalOwner', type: 'address' },
          { name: 'mintingEnabled', type: 'bool' },
          { name: 'hardCap', type: 'uint256' },
          { name: 'pausable', type: 'bool' },
          { name: 'rescueEnabled', type: 'bool' },
          { name: 'burnEnabled', type: 'bool' },
          { name: 'metadataMutable', type: 'bool' },
          { name: 'socialsMutable', type: 'bool' },
          { name: 'feesModuleEnabled', type: 'bool' },
          { name: 'limitsModuleEnabled', type: 'bool' },
          { name: 'cooldownModuleEnabled', type: 'bool' },
          { name: 'blacklistModuleEnabled', type: 'bool' },
          { name: 'website', type: 'string' },
          { name: 'telegram', type: 'string' },
          { name: 'xHandle', type: 'string' },
          { name: 'discord', type: 'string' },
          { name: 'feesEnabled', type: 'bool' },
          { name: 'maxBuyFeeBps', type: 'uint16' },
          { name: 'maxSellFeeBps', type: 'uint16' },
          { name: 'maxTransferFeeBps', type: 'uint16' },
          { name: 'buyFeeBps', type: 'uint16' },
          { name: 'sellFeeBps', type: 'uint16' },
          { name: 'transferFeeBps', type: 'uint16' },
          { name: 'feeBurnShareBps', type: 'uint16' },
          { name: 'feeRecipient', type: 'address' },
          { name: 'maxWalletAmount', type: 'uint256' },
          { name: 'maxTxAmount', type: 'uint256' },
          { name: 'limitsEndTime', type: 'uint64' },
          { name: 'exemptRecipientsFromLimits', type: 'bool' },
          { name: 'additionalLimitExempt', type: 'address[]' },
          { name: 'maxCooldownSeconds', type: 'uint32' },
          { name: 'cooldownSeconds', type: 'uint32' },
          { name: 'initialRecipients', type: 'address[]' },
          { name: 'initialAmounts', type: 'uint256[]' },
          { name: 'initialLabels', type: 'string[]' },
          { name: 'tradingSchedule', type: 'uint64' },
          { name: 'enableTradingImmediately', type: 'bool' },
          { name: 'router', type: 'address' },
          { name: 'routerProtectionEnabled', type: 'bool' },
          { name: 'initialBlacklist', type: 'address[]' },
          { name: 'freezeBlacklistAfterDeploy', type: 'bool' },
          { name: 'renounceOwnership', type: 'bool' },
          { name: 'useSalt', type: 'bool' },
          { name: 'salt', type: 'bytes32' },
          { name: 'deadblocksEnabled', type: 'bool' },
          { name: 'deadBlocks', type: 'uint8' },
          { name: 'deadblockMode', type: 'uint8' },
          { name: 'deadblockExemptLimitExempt', type: 'bool' },
          { name: 'whitelistModuleEnabled', type: 'bool' },
          { name: 'whitelistMode', type: 'uint8' },
          { name: 'whitelistAutoDisable', type: 'bool' },
          { name: 'whitelistBypassesRouter', type: 'bool' },
          { name: 'whitelistMutable', type: 'bool' },
          { name: 'initialWhitelist', type: 'address[]' },
          { name: 'freezeWhitelistAfterDeploy', type: 'bool' },
          { name: 'antiDumpModuleEnabled', type: 'bool' },
          { name: 'antiDumpType', type: 'uint8' },
          { name: 'maxAntiDumpLimit', type: 'uint256' },
          { name: 'maxAntiDumpPeriod', type: 'uint64' },
          { name: 'antiDumpLimit', type: 'uint256' },
          { name: 'antiDumpPeriod', type: 'uint64' },
          { name: 'antiDumpEndTime', type: 'uint64' },
          { name: 'batchTransferAccess', type: 'uint8' },
          { name: 'batchTransferFees', type: 'uint8' },
          { name: 'maxBatchSize', type: 'uint16' },
          { name: 'permitEnabled', type: 'bool' },
          { name: 'freezeFeesAfterDeploy', type: 'bool' },
          { name: 'freezeLimitsAfterDeploy', type: 'bool' },
          { name: 'freezeCooldownAfterDeploy', type: 'bool' },
          { name: 'freezeAntiDumpAfterDeploy', type: 'bool' },
          { name: 'blockOwnershipTransferAfterDeploy', type: 'bool' },
        ],
      },
    ],
    outputs: [{ name: '', type: 'address' }],
    stateMutability: 'payable',
  },
  {
    type: 'function',
    name: 'getCreationFee',
    inputs: [],
    outputs: [{ name: 'fee', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    name: 'predictAddress',
    inputs: [{ name: 'salt', type: 'bytes32' }],
    outputs: [{ name: '', type: 'address' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    name: 'implementation',
    inputs: [],
    outputs: [{ name: '', type: 'address' }],
    stateMutability: 'view',
  },
  {
    type: 'event',
    name: 'TokenCreated',
    inputs: [
      { name: 'token', type: 'address', indexed: true },
      { name: 'owner', type: 'address', indexed: true },
      { name: 'salt', type: 'bytes32', indexed: false },
    ],
    anonymous: false,
  },
] as const;

// ============================================================================
// Wallet Input Types
// ============================================================================

/**
 * EIP-1193 Provider interface (window.ethereum, WalletConnect, etc.)
 */
export interface EIP1193Provider {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
}

/**
 * Wallet input for provider + account (used by Reown, wagmi, etc.)
 */
export interface WalletClientInput {
  /** EIP-1193 compatible provider */
  provider: EIP1193Provider;
  /** Connected account address */
  account: Address;
}

/**
 * Union type for all supported wallet inputs.
 *
 * Supported inputs:
 * - EIP1193Provider: window.ethereum, WalletConnect provider, etc.
 * - ethers.Signer: Already resolved ethers Signer
 * - WalletClientInput: Provider + account object (wagmi/viem style)
 *
 * @example Different wallet sources
 * ```typescript
 * // MetaMask/browser extension
 * const signer = await normalizeWallet(window.ethereum);
 *
 * // Already have an ethers Signer
 * const signer = await normalizeWallet(existingSigner);
 *
 * // wagmi/viem style wallet client
 * const signer = await normalizeWallet({ provider: walletClient, account: '0x...' });
 * ```
 */
export type WalletInput = EIP1193Provider | Signer | WalletClientInput;

// ============================================================================
// Legacy Chains (No EIP-1559)
// ============================================================================

/**
 * Chain IDs that don't support EIP-1559 (use legacy gas pricing).
 * These chains require { type: 0 } in transaction options.
 */
export const LEGACY_CHAIN_IDS = new Set([
  56, // BSC Mainnet
  97, // BSC Testnet
  5000, // Mantle Mainnet
  5003, // Mantle Sepolia
  41455, // Monad Testnet
  10143, // Monad Devnet
  1088, // Metis Mainnet
  59902, // Metis Sepolia
  2222, // Kava Mainnet
  2221, // Kava Testnet
  40, // Telos Mainnet
  41, // Telos Testnet
]);

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if a chain uses legacy gas pricing (no EIP-1559).
 *
 * @param chainId - Chain ID to check
 * @returns True if chain requires legacy transactions
 */
export function isLegacyChain(chainId: number): boolean {
  return LEGACY_CHAIN_IDS.has(chainId);
}

/**
 * Safely parse a string to BigInt, validating that it only contains digits.
 * Allows formatting characters (commas, underscores, spaces).
 *
 * @param value - The string to parse
 * @param fieldName - Name of the field (for error messages)
 * @returns BigInt value
 * @throws Error if the string contains invalid characters
 *
 * @example
 * ```typescript
 * safeParseBigInt('1,000,000', 'supply') // Returns 1000000n
 * safeParseBigInt('1_000_000', 'supply') // Returns 1000000n
 * safeParseBigInt('1 000 000', 'supply') // Returns 1000000n
 * ```
 */
function safeParseBigInt(value: string, fieldName: string): bigint {
  // Remove allowed formatting characters (commas, underscores, spaces)
  const cleaned = value.replace(/[,_\s]/g, '');

  // Validate that the cleaned string only contains digits
  if (!/^\d+$/.test(cleaned)) {
    throw new Error(
      `Invalid ${fieldName}: "${value}" contains non-numeric characters. ` +
        `Only digits and formatting characters (commas, spaces, underscores) are allowed.`,
    );
  }

  try {
    return BigInt(cleaned);
  } catch (error) {
    throw new Error(`Failed to parse ${fieldName}: ${error}`);
  }
}

// ============================================================================
// Wallet Normalization
// ============================================================================

/**
 * Type guard to check if input is an ethers Signer.
 */
function isSigner(input: WalletInput): input is Signer {
  return (
    typeof input === 'object' &&
    input !== null &&
    'getAddress' in input &&
    typeof (input as Signer).getAddress === 'function' &&
    'signMessage' in input &&
    typeof (input as Signer).signMessage === 'function'
  );
}

/**
 * Type guard to check if input is an EIP-1193 Provider.
 */
function isEIP1193Provider(input: WalletInput): input is EIP1193Provider {
  return (
    typeof input === 'object' &&
    input !== null &&
    'request' in input &&
    typeof (input as EIP1193Provider).request === 'function'
  );
}

/**
 * Type guard to check if input is a WalletClientInput.
 */
function isWalletClientInput(input: WalletInput): input is WalletClientInput {
  return (
    typeof input === 'object' &&
    input !== null &&
    'provider' in input &&
    'account' in input &&
    typeof (input as WalletClientInput).account === 'string'
  );
}

/**
 * Normalize any wallet input to an ethers Signer.
 *
 * Handles:
 * - EIP-1193 providers (window.ethereum, WalletConnect)
 * - ethers.Signer (pass-through)
 * - WalletClientInput (provider + account from wagmi/viem)
 *
 * @param wallet - Wallet input in any supported format
 * @returns ethers Signer ready for transactions
 * @throws Error if wallet type is not supported or connection fails
 *
 * @example React with wagmi
 * ```typescript
 * import { useWalletClient } from 'wagmi';
 * import { normalizeWallet } from '@escapehub/token-creator';
 *
 * const { data: walletClient } = useWalletClient();
 * const signer = await normalizeWallet(walletClient);
 * ```
 *
 * @example Vue with ethers
 * ```typescript
 * import { normalizeWallet } from '@escapehub/token-creator';
 *
 * const signer = await normalizeWallet(window.ethereum);
 * ```
 *
 * @example Vanilla JS
 * ```typescript
 * import { normalizeWallet } from '@escapehub/token-creator';
 *
 * // From MetaMask
 * const signer = await normalizeWallet(window.ethereum);
 *
 * // From existing signer (pass-through)
 * const signer = await normalizeWallet(existingSigner);
 * ```
 */
export async function normalizeWallet(wallet: WalletInput): Promise<Signer> {
  // Already an ethers Signer - return as-is
  if (isSigner(wallet)) {
    return wallet;
  }

  // WalletClientInput (provider + account)
  if (isWalletClientInput(wallet)) {
    const provider = new BrowserProvider(wallet.provider as EIP1193Provider);
    return provider.getSigner(wallet.account);
  }

  // EIP-1193 Provider (window.ethereum, etc.)
  if (isEIP1193Provider(wallet)) {
    const provider = new BrowserProvider(wallet);
    // Request accounts if not already connected
    await provider.send('eth_requestAccounts', []);
    return provider.getSigner();
  }

  throw new Error(
    'Unsupported wallet input. Expected EIP-1193 provider, ethers Signer, or { provider, account } object.',
  );
}

// ============================================================================
// Token Configuration Encoding
// ============================================================================

/**
 * Encode TokenConfig into the CreateParams tuple for the factory contract.
 *
 * This function converts the high-level TokenConfig into the exact format
 * expected by the LaunchERC20Factory.createToken() function.
 *
 * @param config - Token configuration
 * @param salt - Optional vanity salt (bytes32 hex string, defaults to zero bytes)
 * @returns CreateParams struct ready for contract call
 *
 * @example
 * ```typescript
 * import { encodeTokenConfig, createDefaultConfig } from '@escapehub/token-creator';
 *
 * const config = createDefaultConfig('My Token', 'MTK', '1000000', '0x...');
 * const params = encodeTokenConfig(config);
 * // params is ready for contract.createToken(params)
 * ```
 */
export function encodeTokenConfig(
  config: TokenConfig,
  salt: string = '',
): Record<string, unknown> {
  // Convert human-readable supply to smallest units
  const factor = 10n ** BigInt(config.decimals);
  const totalSupply = safeParseBigInt(config.totalSupplyHuman, 'totalSupplyHuman') * factor;

  // Hard cap (also in smallest units)
  const hardCap = config.hardCapHuman
    ? safeParseBigInt(config.hardCapHuman, 'hardCapHuman') * factor
    : 0n;

  // Determine if using vanity salt
  const useSalt = salt !== '0x' + '0'.repeat(64) && salt !== '';
  const saltBytes = useSalt ? salt : '0x' + '0'.repeat(64);

  // Fee recipient - must not be zero address if fees are enabled
  let feeRecipient: Address = ZERO_ADDRESS;
  if (config.feesModuleEnabled && config.feesEnabled) {
    // Only set a non-zero recipient if burn share is not 100%
    if ((config.feeBurnShareBps ?? 0) < 10000) {
      feeRecipient =
        config.feeRecipient && config.feeRecipient !== ZERO_ADDRESS
          ? config.feeRecipient
          : config.finalOwner;
    }
  }

  return {
    // Core ERC20
    name: config.name,
    symbol: config.symbol,
    decimals: config.decimals,
    initialSupply: totalSupply.toString(),
    finalOwner: config.finalOwner,
    // Core features
    mintingEnabled: config.mintable ?? false,
    hardCap: hardCap.toString(),
    pausable: config.pausable ?? false,
    rescueEnabled: config.rescueEnabled ?? false,
    burnEnabled: config.burnEnabled ?? false,
    metadataMutable: config.metadataMutable ?? false,
    socialsMutable: config.socialsMutable ?? false,
    // Optional modules
    feesModuleEnabled: config.feesModuleEnabled ?? false,
    limitsModuleEnabled: config.limitsModuleEnabled ?? false,
    cooldownModuleEnabled: config.cooldownModuleEnabled ?? false,
    blacklistModuleEnabled: config.blacklistModuleEnabled ?? false,
    // Socials
    website: config.website ?? '',
    telegram: config.telegram ?? '',
    xHandle: config.xHandle ?? '',
    discord: config.discord ?? '',
    // Fees config
    feesEnabled: config.feesEnabled ?? false,
    maxBuyFeeBps: config.maxBuyFeeBps ?? 0,
    maxSellFeeBps: config.maxSellFeeBps ?? 0,
    maxTransferFeeBps: config.maxTransferFeeBps ?? 0,
    buyFeeBps: config.buyFeeBps ?? 0,
    sellFeeBps: config.sellFeeBps ?? 0,
    transferFeeBps: config.transferFeeBps ?? 0,
    feeBurnShareBps: config.feeBurnShareBps ?? 0,
    feeRecipient: feeRecipient,
    // Limits config
    maxWalletAmount: config.maxWalletAmount ?? '0',
    maxTxAmount: config.maxTxAmount ?? '0',
    limitsEndTime: config.limitsEndTime ?? 0,
    exemptRecipientsFromLimits: config.exemptRecipientsFromLimits ?? false,
    additionalLimitExempt: config.additionalLimitExempt ?? [],
    // Cooldown config
    maxCooldownSeconds: config.maxCooldownSeconds ?? 0,
    cooldownSeconds: config.cooldownSeconds ?? 0,
    // Initial distribution
    initialRecipients: config.initialRecipients ?? [],
    initialAmounts: config.initialAmounts ?? [],
    initialLabels: config.initialLabels ?? [],
    // Trading schedule
    tradingSchedule: config.tradingSchedule ?? 0,
    // Trading enable immediately
    enableTradingImmediately: config.enableTradingImmediately ?? false,
    // Router protection
    router: config.router ?? ZERO_ADDRESS,
    routerProtectionEnabled: config.routerProtectionEnabled ?? false,
    // Blacklist
    initialBlacklist: config.initialBlacklist ?? [],
    freezeBlacklistAfterDeploy: config.freezeBlacklistAfterDeploy ?? false,
    // Ownership
    renounceOwnership: config.renounceOwnership ?? false,
    // Vanity deployment
    useSalt: useSalt,
    salt: saltBytes,
    // Deadblock config
    deadblocksEnabled: config.deadblocksEnabled ?? false,
    deadBlocks: config.deadBlocks ?? 0,
    deadblockMode: config.deadblockMode ?? 0,
    deadblockExemptLimitExempt: config.deadblockExemptLimitExempt ?? false,
    // Whitelist config
    whitelistModuleEnabled: config.whitelistModuleEnabled ?? false,
    whitelistMode: config.whitelistMode ?? 0,
    whitelistAutoDisable: config.whitelistAutoDisable ?? false,
    whitelistBypassesRouter: config.whitelistBypassesRouter ?? false,
    whitelistMutable: config.whitelistMutable ?? false,
    initialWhitelist: config.initialWhitelist ?? [],
    freezeWhitelistAfterDeploy: config.freezeWhitelistAfterDeploy ?? false,
    // Anti-dump config
    antiDumpModuleEnabled: config.antiDumpModuleEnabled ?? false,
    antiDumpType: config.antiDumpType ?? 0,
    maxAntiDumpLimit: config.maxAntiDumpLimit ?? '0',
    maxAntiDumpPeriod: config.maxAntiDumpPeriod ?? 0,
    antiDumpLimit: config.antiDumpLimit ?? '0',
    antiDumpPeriod: config.antiDumpPeriod ?? 0,
    antiDumpEndTime: config.antiDumpEndTime ?? 0,
    // Batch transfer config
    batchTransferAccess: config.batchTransferAccess ?? 0,
    batchTransferFees: config.batchTransferFees ?? 0,
    maxBatchSize: config.maxBatchSize ?? 100,
    // Permit config
    permitEnabled: config.permitEnabled ?? true,
    // Freeze after deploy config
    freezeFeesAfterDeploy: config.freezeFeesAfterDeploy ?? false,
    freezeLimitsAfterDeploy: config.freezeLimitsAfterDeploy ?? false,
    freezeCooldownAfterDeploy: config.freezeCooldownAfterDeploy ?? false,
    freezeAntiDumpAfterDeploy: config.freezeAntiDumpAfterDeploy ?? false,
    // Ownership transfer blocking
    blockOwnershipTransferAfterDeploy: config.blockOwnershipTransferAfterDeploy ?? false,
  };
}

// ============================================================================
// Creation Fee
// ============================================================================

/**
 * Get the creation fee required by the factory contract.
 *
 * The factory charges a small fee (~$1 in native tokens) for each token deployment.
 * This fee is sent as msg.value when calling createToken().
 *
 * @param provider - ethers Provider or Signer with provider
 * @param factoryAddress - Factory contract address
 * @returns Creation fee in wei (native token smallest unit)
 *
 * @example
 * ```typescript
 * import { getCreationFee } from '@escapehub/token-creator';
 * import { JsonRpcProvider, formatEther } from 'ethers';
 *
 * const provider = new JsonRpcProvider('https://rpc.sepolia.org');
 * const fee = await getCreationFee(provider, '0x...');
 * console.log('Fee:', formatEther(fee), 'ETH');
 * ```
 */
export async function getCreationFee(
  provider: Provider | Signer,
  factoryAddress: Address,
): Promise<bigint> {
  const contract = new Contract(factoryAddress, FACTORY_ABI, provider);
  return await contract.getCreationFee();
}

// ============================================================================
// Gas Estimation
// ============================================================================

/**
 * Estimate gas for token deployment.
 *
 * Returns both the estimated gas limit and the creation fee required.
 * Use this to display estimated costs to users before deployment.
 *
 * @param provider - ethers Provider or Signer (must have a provider)
 * @param factoryAddress - Factory contract address
 * @param config - Token configuration
 * @param salt - Optional vanity salt (bytes32 hex string)
 * @returns Gas estimation including factory creation fee
 *
 * @example
 * ```typescript
 * import { estimateGas, createDefaultConfig } from '@escapehub/token-creator';
 * import { formatEther, formatUnits } from 'ethers';
 *
 * const config = createDefaultConfig('My Token', 'MTK', '1000000', '0x...');
 * const estimation = await estimateGas(provider, '0x...', config);
 *
 * console.log('Gas limit:', estimation.gasLimit.toString());
 * console.log('Creation fee:', formatEther(estimation.creationFee), 'ETH');
 * ```
 */
export async function estimateGas(
  provider: Provider | Signer,
  factoryAddress: Address,
  config: TokenConfig,
  salt: string = '',
): Promise<GasEstimation> {
  const contract = new Contract(factoryAddress, FACTORY_ABI, provider);

  // Build CreateParams struct
  const createParams = encodeTokenConfig(config, salt);

  // Get creation fee
  let creationFee = 0n;
  try {
    creationFee = await contract.getCreationFee();
  } catch {
    // Assume free if cannot fetch
  }

  // Estimate gas
  const gasLimit = await contract.createToken.estimateGas(createParams, {
    value: creationFee,
  });

  return { gasLimit, creationFee };
}

// ============================================================================
// Token Deployment
// ============================================================================

/**
 * Deploy a new token using the LaunchERC20Factory.
 *
 * This is the main deployment function. It:
 * 1. Encodes the token configuration
 * 2. Fetches the creation fee
 * 3. Sends the createToken transaction
 * 4. Waits for confirmation
 * 5. Extracts the token address from events
 *
 * Handles legacy chains (no EIP-1559) automatically.
 *
 * @param wallet - Wallet input (will be normalized to Signer)
 * @param config - Token configuration
 * @param chainConfig - Chain configuration with factory address
 * @param salt - Optional vanity salt (bytes32 hex string)
 * @returns Deployment result with transaction hash and token address
 *
 * @example React with wagmi
 * ```tsx
 * import { deployToken, createDefaultConfig } from '@escapehub/token-creator';
 * import { useWalletClient } from 'wagmi';
 *
 * function DeployButton() {
 *   const { data: walletClient } = useWalletClient();
 *
 *   const handleDeploy = async () => {
 *     const config = createDefaultConfig(
 *       'My Token',
 *       'MTK',
 *       '1000000',
 *       await walletClient.account.address
 *     );
 *
 *     const result = await deployToken(
 *       walletClient,
 *       config,
 *       { chainId: 11155111, factoryAddress: '0x...' }
 *     );
 *
 *     console.log('Token deployed at:', result.tokenAddress);
 *     console.log('Transaction:', result.txHash);
 *   };
 *
 *   return <button onClick={handleDeploy}>Deploy Token</button>;
 * }
 * ```
 *
 * @example Vue with ethers
 * ```vue
 * <script setup>
 * import { deployToken, normalizeWallet, createDefaultConfig } from '@escapehub/token-creator';
 *
 * const deploy = async () => {
 *   const signer = await normalizeWallet(window.ethereum);
 *   const address = await signer.getAddress();
 *
 *   const config = createDefaultConfig('My Token', 'MTK', '1000000', address);
 *
 *   const result = await deployToken(
 *     signer,
 *     config,
 *     { chainId: 11155111, factoryAddress: '0x...' }
 *   );
 *
 *   console.log('Token deployed at:', result.tokenAddress);
 * };
 * </script>
 * ```
 *
 * @example Vanilla JS
 * ```javascript
 * import { deployToken, normalizeWallet, createDefaultConfig } from '@escapehub/token-creator';
 *
 * async function deploy() {
 *   const signer = await normalizeWallet(window.ethereum);
 *   const address = await signer.getAddress();
 *
 *   const config = createDefaultConfig('My Token', 'MTK', '1000000', address);
 *
 *   const result = await deployToken(
 *     signer,
 *     config,
 *     { chainId: 11155111, factoryAddress: '0x...' }
 *   );
 *
 *   console.log('Token deployed at:', result.tokenAddress);
 * }
 * ```
 */
export async function deployToken(
  wallet: WalletInput,
  config: TokenConfig,
  chainConfig: ChainConfig,
  salt: string = '',
): Promise<DeploymentResult> {
  if (!chainConfig.factoryAddress) {
    throw new Error('Missing factory address in chain configuration');
  }

  // Normalize wallet to ethers Signer
  const signer = await normalizeWallet(wallet);
  const contract = new Contract(chainConfig.factoryAddress, FACTORY_ABI, signer);

  // Build CreateParams struct (finalOwner is in config)
  const createParams = encodeTokenConfig(config, salt);

  // Get required creation fee from factory
  let creationFee = 0n;
  try {
    creationFee = await contract.getCreationFee();
  } catch (e) {
    console.warn('Could not fetch creation fee, assuming free:', e);
  }

  // Build transaction options
  const txOptions: { value: bigint; type?: number } = { value: creationFee };

  // Use legacy transaction type for chains without EIP-1559
  if (isLegacyChain(chainConfig.chainId)) {
    txOptions.type = 0;
  }

  // Send transaction with fee as msg.value
  const tx = await contract.createToken(createParams, txOptions);
  const receipt = await tx.wait();

  // Extract token address from TokenCreated event
  const tokenAddress = parseTokenCreatedEvent(receipt, chainConfig.factoryAddress as Address);

  if (!tokenAddress) {
    throw new Error('Token created but could not extract address from event');
  }

  return { txHash: receipt.hash as `0x${string}`, tokenAddress };
}

// ============================================================================
// Address Prediction
// ============================================================================

/**
 * Predict the token address that would be created with a given salt.
 *
 * Uses the factory's predictAddress function via RPC call.
 * For local prediction without RPC, use predictAddressLocal().
 *
 * @param provider - ethers Provider or Signer
 * @param factoryAddress - Factory contract address
 * @param salt - Vanity salt (bytes32 hex string)
 * @returns Predicted token address
 *
 * @example
 * ```typescript
 * import { predictAddress } from '@escapehub/token-creator';
 *
 * const address = await predictAddress(
 *   provider,
 *   '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
 *   '0x0000000000000000000000000000000000000000000000000000000000000001'
 * );
 * console.log('Token will be deployed at:', address);
 * ```
 */
export async function predictAddress(
  provider: Provider | Signer,
  factoryAddress: Address,
  salt: string,
): Promise<Address> {
  const contract = new Contract(factoryAddress, FACTORY_ABI, provider);
  return (await contract.predictAddress(salt)) as Address;
}

/**
 * Compute the init code hash for OpenZeppelin Clones minimal proxy.
 * Used for local CREATE2 address prediction without RPC calls.
 *
 * @param implementationAddress - The implementation contract address
 * @returns The init code hash for CREATE2 address prediction
 *
 * @example
 * ```typescript
 * import { getMinimalProxyInitCodeHash } from '@escapehub/token-creator';
 *
 * const initCodeHash = getMinimalProxyInitCodeHash('0x...');
 * // Can be used with predictAddressLocal
 * ```
 */
export function getMinimalProxyInitCodeHash(implementationAddress: Address): string {
  // Normalize implementation address (remove 0x, lowercase)
  const impl = implementationAddress.toLowerCase().replace('0x', '');

  if (impl.length !== 40) {
    throw new Error('Invalid implementation address');
  }

  // OpenZeppelin Clones minimal proxy bytecode:
  // prefix (10 bytes) + implementation (20 bytes) + suffix (15 bytes) = 45 bytes
  const prefix = '3d602d80600a3d3981f3363d3d373d3d3d363d73';
  const suffix = '5af43d82803e903d91602b57fd5bf3';

  const initCode = '0x' + prefix + impl + suffix;
  const initCodeHash = keccak256(getBytes(initCode));

  return initCodeHash;
}

/**
 * Predict token address locally using CREATE2 formula.
 * Does not require RPC call - useful for vanity address mining.
 *
 * @param factoryAddress - Factory contract address
 * @param salt - Vanity salt (bytes32 hex string)
 * @param initCodeHash - Init code hash (from getMinimalProxyInitCodeHash)
 * @returns Predicted token address
 *
 * @example
 * ```typescript
 * import { predictAddressLocal, getMinimalProxyInitCodeHash } from '@escapehub/token-creator';
 *
 * const initCodeHash = getMinimalProxyInitCodeHash(implementationAddress);
 * const address = predictAddressLocal(
 *   '0x10ca27bc0c4f9c1ea9169a76270c0c136d160a54',
 *   '0x0000000000000000000000000000000000000000000000000000000000000001',
 *   initCodeHash
 * );
 * ```
 */
export function predictAddressLocal(
  factoryAddress: Address,
  salt: string,
  initCodeHash: string,
): Address {
  return getCreate2Address(factoryAddress, salt, initCodeHash) as Address;
}

// ============================================================================
// Transaction Waiting
// ============================================================================

/**
 * Wait for a deployment transaction to be confirmed.
 *
 * @param provider - ethers Provider or Signer with provider
 * @param txHash - Transaction hash to wait for
 * @param confirmations - Number of confirmations to wait for (default: 1)
 * @returns Transaction receipt
 * @throws Error if transaction fails or times out
 *
 * @example
 * ```typescript
 * import { waitForDeployment } from '@escapehub/token-creator';
 *
 * // Wait for 1 confirmation (default)
 * const receipt = await waitForDeployment(provider, txHash);
 *
 * // Wait for 3 confirmations
 * const receipt = await waitForDeployment(provider, txHash, 3);
 * ```
 */
export async function waitForDeployment(
  provider: Provider | Signer,
  txHash: string,
  confirmations: number = 1,
): Promise<TransactionReceipt> {
  // Get provider from signer if needed
  let actualProvider: Provider;
  if ('provider' in provider && provider.provider) {
    actualProvider = provider.provider;
  } else if ('getTransaction' in provider) {
    actualProvider = provider as Provider;
  } else {
    throw new Error('Provider or Signer with provider required');
  }

  const receipt = await actualProvider.waitForTransaction(txHash, confirmations);

  if (!receipt) {
    throw new Error(`Transaction ${txHash} not found`);
  }

  if (receipt.status === 0) {
    throw new Error(`Transaction ${txHash} failed`);
  }

  return receipt;
}

// ============================================================================
// Event Parsing
// ============================================================================

/**
 * Parse TokenCreated event from a transaction receipt.
 *
 * Extracts the deployed token address from the factory's TokenCreated event.
 *
 * @param receipt - Transaction receipt from deployment
 * @param factoryAddress - Factory contract address (for event filtering)
 * @returns Token address if found, null otherwise
 *
 * @example
 * ```typescript
 * import { parseTokenCreatedEvent } from '@escapehub/token-creator';
 *
 * const receipt = await provider.waitForTransaction(txHash);
 * const tokenAddress = parseTokenCreatedEvent(receipt, factoryAddress);
 *
 * if (tokenAddress) {
 *   console.log('Token deployed at:', tokenAddress);
 * } else {
 *   console.log('TokenCreated event not found in receipt');
 * }
 * ```
 */
export function parseTokenCreatedEvent(
  receipt: TransactionReceipt,
  factoryAddress: Address,
): Address | null {
  const iface = new Interface(FACTORY_ABI);

  for (const log of receipt.logs) {
    // Filter by factory address (optional but recommended)
    if (log.address.toLowerCase() !== factoryAddress.toLowerCase()) {
      continue;
    }

    try {
      const parsed = iface.parseLog({
        topics: log.topics as string[],
        data: log.data,
      });

      if (parsed?.name === 'TokenCreated') {
        return parsed.args.token as Address;
      }
    } catch {
      // Not our event, skip
    }
  }

  return null;
}

// ============================================================================
// Additional Utilities
// ============================================================================

/**
 * Get the implementation address used by the factory.
 *
 * The factory uses OpenZeppelin Clones to deploy minimal proxies
 * pointing to this implementation.
 *
 * @param provider - ethers Provider or Signer
 * @param factoryAddress - Factory contract address
 * @returns Implementation contract address
 *
 * @example
 * ```typescript
 * import { getImplementation } from '@escapehub/token-creator';
 *
 * const impl = await getImplementation(provider, '0x...');
 * console.log('Implementation:', impl);
 * ```
 */
export async function getImplementation(
  provider: Provider | Signer,
  factoryAddress: Address,
): Promise<Address> {
  const contract = new Contract(factoryAddress, FACTORY_ABI, provider);
  return (await contract.implementation()) as Address;
}

/**
 * Check if an address already has deployed code.
 * Returns true if address has code (deployment would fail).
 *
 * @param provider - ethers Provider or Signer with provider
 * @param address - Address to check
 * @returns True if address has code
 *
 * @example
 * ```typescript
 * import { addressHasCode } from '@escapehub/token-creator';
 *
 * const hasCode = await addressHasCode(provider, '0x...');
 * if (hasCode) {
 *   console.log('Address already has a contract!');
 * }
 * ```
 */
export async function addressHasCode(
  provider: Provider | Signer,
  address: Address,
): Promise<boolean> {
  // Get provider from signer if needed
  let actualProvider: Provider;
  if ('provider' in provider && provider.provider) {
    actualProvider = provider.provider;
  } else if ('getCode' in provider) {
    actualProvider = provider as Provider;
  } else {
    throw new Error('Provider or Signer with provider required');
  }

  const code = await actualProvider.getCode(address);
  return code !== '0x' && code !== '0x0' && code.length > 2;
}

/**
 * Verify that an address is available for deployment.
 * Throws an error if the address already has code.
 *
 * @param provider - ethers Provider or Signer
 * @param factoryAddress - Factory contract address
 * @param salt - Vanity salt (bytes32 hex string)
 * @returns Predicted address if available
 * @throws Error if address already has code
 *
 * @example
 * ```typescript
 * import { verifyAddressAvailable } from '@escapehub/token-creator';
 *
 * try {
 *   const address = await verifyAddressAvailable(provider, factoryAddress, salt);
 *   console.log('Address available:', address);
 * } catch (e) {
 *   console.log('Address already taken!');
 * }
 * ```
 */
export async function verifyAddressAvailable(
  provider: Provider | Signer,
  factoryAddress: Address,
  salt: string,
): Promise<Address> {
  const predicted = await predictAddress(provider, factoryAddress, salt);
  const hasCode = await addressHasCode(provider, predicted);

  if (hasCode) {
    throw new Error(
      `Address ${predicted} already has a deployed contract. ` +
        `Please use a different vanity pattern or disable vanity address.`,
    );
  }

  return predicted;
}

/**
 * Create a read-only provider for a chain.
 * Useful when you only need to read data without a wallet.
 *
 * @param rpcUrl - RPC endpoint URL
 * @returns JsonRpcProvider instance
 *
 * @example
 * ```typescript
 * import { createProvider, getCreationFee } from '@escapehub/token-creator';
 *
 * const provider = createProvider('https://rpc.sepolia.org');
 * const fee = await getCreationFee(provider, '0x...');
 * ```
 */
export function createProvider(rpcUrl: string): JsonRpcProvider {
  return new JsonRpcProvider(rpcUrl);
}

// ============================================================================
// Re-exports
// ============================================================================

export type {
  TokenConfig,
  Address,
} from './types/deploy-config';

export type { ChainConfig } from './types/token-data';

export {
  ZERO_ADDRESS,
  DeadblockMode,
  WhitelistMode,
  AntiDumpType,
  BatchTransferAccess,
  BatchTransferFees,
  createDefaultConfig,
} from './types/deploy-config';
